// Studio URL builders — the single source of truth for the route scheme.
//
// Studio owns the ROOT namespace of archlang.dev (the github.com model): a
// workspace lives at the top level, and its URL is also its git clone URL.
// `deploy/routing.json` carves out the paths the static site tier serves and
// generates the reserved-slug list that keeps a workspace from taking one.
//
//   /:ws                               workspace home (repo list)
//   /:ws/_/settings[/tokens|/trash]    workspace settings
//   /:ws/_/members                     workspace members
//   /:ws/:repo                         repo home → resolves the unnamed
//                                      draft, replaces to /:num
//   /:ws/:repo/_/settings              repo settings
//   /:ws/:repo/_/proposals             proposal list
//   /:ws/:repo/_/history/:ref          history
//   /:ws/:repo/_/commit/:oid           commit
//   /:ws/:repo/_/files/:ref/*          file browser
//   /:ws/:repo/_/diff                  diff page
//   /:ws/:repo/:num                    changeset :num — editor/review
//   /:ws/:repo/:num/<path>             changeset :num — file open
//
// Disambiguation: the first segment after :repo is either a number (a
// changeset) or the literal `_` (a page). `_` is the only reserved ws/repo
// name. React-router ranks the static `_` above the dynamic `:num`.

const enc = encodeURIComponent;

export const wsHome = (ws: string): string => `/${enc(ws)}`;
export const wsSettings = (ws: string): string => `/${enc(ws)}/_/settings`;
export const wsTokens = (ws: string): string => `/${enc(ws)}/_/settings/tokens`;
export const wsTrash = (ws: string): string => `/${enc(ws)}/_/settings/trash`;
export const wsMembers = (ws: string): string => `/${enc(ws)}/_/members`;

export const repoHome = (ws: string, repo: string): string => `/${enc(ws)}/${enc(repo)}`;

export const repoSettings = (ws: string, repo: string): string =>
  `/${enc(ws)}/${enc(repo)}/_/settings`;
export const repoProposals = (ws: string, repo: string): string =>
  `/${enc(ws)}/${enc(repo)}/_/proposals`;
export const repoHistory = (ws: string, repo: string, ref: string): string =>
  `/${enc(ws)}/${enc(repo)}/_/history/${enc(ref)}`;
export const repoCommit = (ws: string, repo: string, oid: string): string =>
  `/${enc(ws)}/${enc(repo)}/_/commit/${enc(oid)}`;
export const repoFiles = (ws: string, repo: string, ref: string, path = ""): string =>
  `/${enc(ws)}/${enc(repo)}/_/files/${enc(ref)}${path ? `/${path}` : ""}`;
export const repoDiff = (ws: string, repo: string): string =>
  `/${enc(ws)}/${enc(repo)}/_/diff`;

/** Changeset editor/review. `path` is a repo-relative file path (already `/`-joined). */
export const changeset = (ws: string, repo: string, num: number, path = ""): string =>
  `/${enc(ws)}/${enc(repo)}/${num}${path ? `/${path}` : ""}`;
